import { useTranslation } from 'react-i18next';
import type { CrisisProtocol } from '../api.ts';

/**
 * Il protocollo di crisi dell'ente, come si legge.
 *
 * "Il volontario non deve mai vedere solo «nessuno disponibile»: deve vedere
 * cosa fare adesso" (sezione 5.9). Quindi: i passi scritti dall'ente, il numero
 * del reperibile telefonico come pulsante che chiama davvero, e il numero unico
 * di emergenza in fondo.
 *
 * Il numero del reperibile **non è mai visibile agli utenti** (mock 3): esce
 * solo verso una sessione staff autenticata, ed è cifrato a riposo come ogni
 * altro dato personale dello staff (AC7).
 *
 * Il caso peggiore — nessun reperibile e nessun protocollo scritto — è quello
 * su cui questa scheda va misurata, perché è l'unico in cui il volontario ha
 * solo questa davanti: la galleria lo disegna in `console-escalating-alone`, e
 * il contrasto lo misura `console-contrast.spec.ts` in tutte e due le palette.
 * Lì l'ordine è quello che conta: prima **cosa fare**, poi chi chiamare, e il
 * numero di emergenza per ultimo. Le negazioni — "l'ente non ne ha scritto
 * uno", "nessun reperibile configurato" — non aprono mai il riquadro e non
 * hanno il peso dei passi: sono la riga che spiega, non la risposta.
 */

type Props = {
  protocol: CrisisProtocol | null;
};

export function CrisisProtocolCard({ protocol }: Props) {
  const { t } = useTranslation();

  return (
    <section className="card" aria-labelledby="protocol-card-heading">
      <h3 id="protocol-card-heading">{t('protocol.title')}</h3>

      {protocol && protocol.steps.length > 0 ? (
        <ol className="protocol-steps">
          {protocol.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      ) : (
        /*
         * L'ente non ne ha ancora scritto uno (AC9), e questa schermata non può
         * limitarsi a dirlo.
         *
         * "Il volontario non deve mai vedere solo «nessuno disponibile»: deve
         * vedere cosa fare adesso" (sezione 5.9). Senza protocollo e senza
         * reperibile, la schermata di crisi diventava tre negazioni di fila
         * dopo aver promesso "qui sotto c'è cosa fare". Questi passi non
         * dipendono dalla configurazione di nessun ente: valgono sempre, e
         * quello dell'ente li sostituisce appena esiste.
         */
        <>
          {/*
            `.card__sub` e non `.muted`: è la riga che spiega la scheda, e
            `.muted` la disegnava con lo stesso grigio delle note in fondo alla
            pagina — cioè con lo stesso peso dei passi che vengono dopo, che
            sono la sola cosa da leggere qui.
          */}
          <p className="card__sub">{t('protocol.none')}</p>
          <h4>{t('protocol.fallback_title')}</h4>
          <ol className="protocol-steps">
            <li>{t('protocol.fallback_1')}</li>
            <li>{t('protocol.fallback_2')}</li>
            <li>{t('protocol.fallback_3')}</li>
            <li>{t('protocol.fallback_4')}</li>
            <li>{t('protocol.fallback_5')}</li>
          </ol>
        </>
      )}

      {protocol?.onCallPhone ? (
        /*
         * Un collegamento `tel:` e non un numero da ricopiare: si preme mentre
         * si ha già qualcuno in linea. Resta un bersaglio da 44 pixel.
         */
        <a
          className="button button--wide button--danger"
          href={`tel:${protocol.onCallPhone.replace(/\s+/g, '')}`}
        >
          {t('protocol.on_call_call', { number: protocol.onCallPhone })}
        </a>
      ) : (
        <p className="muted">{t('protocol.on_call_none')}</p>
      )}

      {/*
        Il numero di emergenza **non** si ripete qui.

        Questa carta compare solo mentre una conversazione è aperta — la monta
        il pannello di supervisione, che a sua volta esiste solo con una
        sessione attiva — e la schermata della conversazione quel numero lo
        porta già, in fondo, in ogni chiamata. Con il pannello aperto lo stesso
        avviso ambra compariva due volte nella stessa schermata, a mezzo
        schermo di distanza: nel momento in cui una persona ha più bisogno di
        capire cosa fare, due avvisi identici non sono il doppio
        dell'informazione, sono metà della fiducia in quello che si legge.

        Quello che è proprio di questa carta resta: i passi scritti dall'ente e
        il numero del reperibile, che sono le due cose che qui sopra non ci
        sono.
      */}
    </section>
  );
}
